import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./admin-page.css";
import WplButton from "../../components/wpl-button/WplButton";
import WplInput from "../../components/wpl-input/WplInput";
import WplPopup from "../../components/wpl-popup-window/WplPopup";
import WplTabs from "../../components/wpl-tabs/WplTabs";
import WplSwitch from "../../components/wpl-switch/wplSwitch";
import AdminHeader from "./admin-header/AdminHeader";
import { SortableTh, sortRows, useClientSort, nextSort } from "./SortableTable";
import WindfarmToggleList from "./WindfarmToggleList";
import { datamanagementAPI, userAPI, windfarmAPI } from "../../api";
import { toPrettyDateStr, toPrettyTimeSince } from "../../prettyDate";

const TABS = ["Users", "Invitations", "Logins", "Deleted users"];
const LOGIN_PAGE_SIZE = 50;
const SCOPES = ["wind-diagnostics", "rocky", "employee", "developer"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const byName = (a, b) => a.name.localeCompare(b.name);
const byCustomerThenName = (a, b) =>
  a.customer !== b.customer ? (a.customer || "").localeCompare(b.customer || "") : a.name.localeCompare(b.name);

function formatLastLogin(ts) {
  if (!ts) return "Never";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "Never";
  return toPrettyTimeSince(d);
}

function errorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  return fallback;
}

function ScopeBadge({ scope }) {
  return <span className={`scope-badge scope-${scope}`}>{scope || "—"}</span>;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return <WplButton className="small" value={copied ? "Copied" : "Copy"} onClick={copy} />;
}

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [allWindfarms, setAllWindfarms] = useState([]);
  const [allRockyOrganizations, setAllRockyOrganizations] = useState([]);
  const [allLassieOrganizations, setAllLassieOrganizations] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedTab, setSelectedTab] = useState(TABS[0]);
  const [loginsUserId, setLoginsUserId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [result, setResult] = useState(null); // { title, text, link }

  const reloadUsers = useCallback(() => userAPI.listUsers(false).then(setUsers), []);
  const reloadInvitations = useCallback(() => userAPI.listInvitations().then(setInvitations), []);

  useEffect(() => {
    Promise.all([
      reloadUsers(),
      reloadInvitations(),
      windfarmAPI.listAllWindfarms().then(setAllWindfarms),
      datamanagementAPI.listRockyOrganizations().then(setAllRockyOrganizations),
      datamanagementAPI.listLassieOrganizations().then(setAllLassieOrganizations),
    ]).finally(() => setLoading(false));
    userAPI.me().then(setMe).catch(() => setMe(null));
  }, [reloadUsers, reloadInvitations]);

  const canDelete = Boolean(me && (me.is_super_admin || me.role === "organization_owner"));

  function viewLoginsForUser(userId) {
    setLoginsUserId(userId);
    setSelectedTab("Logins");
  }
  const activeUsers = useMemo(() => users.filter((u) => !u.is_deleted), [users]);
  const deletedUsers = useMemo(() => users.filter((u) => u.is_deleted), [users]);
  const sortedWindfarms = useMemo(() => [...allWindfarms].sort(byCustomerThenName), [allWindfarms]);

  function confirmDelete() {
    const target = deletingUser;
    return userAPI
      .softDeleteUser(target.id)
      .then(() => {
        setDeletingUser(null);
        if (editingUser && editingUser.id === target.id) setEditingUser(null);
        return reloadUsers();
      })
      .catch((err) => alert(errorMessage(err, "Could not delete the user.")));
  }

  return (
    <div className="admin-page">
      <div className="admin-wrapper">
        <AdminHeader />

        <div className="admin-title-row">
          <h1>User management</h1>
          <WplButton className="primary" value="Create invitation" onClick={() => setShowCreate(true)} />
        </div>

        <WplTabs tabTitles={TABS} selected={selectedTab} setSelected={setSelectedTab} />

        <div className="admin-content">
          {loading && <p className="empty-state">Loading…</p>}
          {!loading && selectedTab === "Users" && (
            <UsersSection
              users={activeUsers}
              canDelete={canDelete}
              me={me}
              onEdit={setEditingUser}
              onDelete={setDeletingUser}
              onViewLogins={viewLoginsForUser}
            />
          )}
          {!loading && selectedTab === "Invitations" && (
            <InvitationsSection invitations={invitations} users={users} onShowLink={setResult} />
          )}
          {!loading && selectedTab === "Logins" && (
            <LoginsSection userId={loginsUserId} onClearUser={() => setLoginsUserId(null)} />
          )}
          {!loading && selectedTab === "Deleted users" && <DeletedUsersSection users={deletedUsers} />}
        </div>
      </div>

      <WplPopup className="admin-popup" showPopup={showCreate} closePopup={setShowCreate}>
        {showCreate && (
          <CreateInvitationForm
            windfarms={allWindfarms}
            rockyOrganizations={allRockyOrganizations}
            lassieOrganizations={allLassieOrganizations}
            onDone={(res) => {
              setShowCreate(false);
              setResult(res);
              reloadUsers();
              reloadInvitations();
            }}
          />
        )}
      </WplPopup>

      <WplPopup className="admin-popup" showPopup={Boolean(editingUser)} closePopup={() => setEditingUser(null)}>
        {editingUser && (
          <EditUserPanel
            user={editingUser}
            windfarms={sortedWindfarms}
            canDelete={canDelete && me && me.email !== editingUser.email}
            onUsersChanged={reloadUsers}
            onDelete={() => setDeletingUser(editingUser)}
            onClose={() => setEditingUser(null)}
          />
        )}
      </WplPopup>

      <WplPopup className="admin-popup small-popup" showPopup={Boolean(deletingUser)} closePopup={() => setDeletingUser(null)}>
        {deletingUser && (
          <div className="confirm-panel">
            <h2>Delete user</h2>
            <p>
              <strong>{deletingUser.firstname || deletingUser.email}</strong> ({deletingUser.email}) will no longer be able to
              log in and their Lassie scenario subscriptions will be removed. Their reports and history are kept, and
              the email address becomes available for a new invitation.
            </p>
            <div className="panel-actions">
              <WplButton className="subtle" value="Cancel" onClick={() => setDeletingUser(null)} />
              <WplButton className="danger" value="Delete user" onClick={confirmDelete} />
            </div>
          </div>
        )}
      </WplPopup>

      <WplPopup className="admin-popup small-popup" showPopup={Boolean(result)} closePopup={() => setResult(null)}>
        {result && (
          <div className="result-panel">
            <h2>{result.title}</h2>
            {result.text && <p>{result.text}</p>}
            {result.link && (
              <div className="link-display">
                <code>{result.link}</code>
                <CopyButton text={result.link} />
              </div>
            )}
            <div className="panel-actions">
              <WplButton className="primary" value="Close" onClick={() => setResult(null)} />
            </div>
          </div>
        )}
      </WplPopup>
    </div>
  );
}

// --------------------------------------------------------------- Logins tab

// Server-side paging and sorting: the history is too large to load whole.
function LoginsSection({ userId, onClearUser }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "timestamp_created", dir: "desc" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setOffset(0);
  }, [search, userId, sort]);

  useEffect(() => {
    setLoading(true);
    setError("");
    userAPI
      .listLogins({ limit: LOGIN_PAGE_SIZE, offset, search: search || null, userId, sortBy: sort.key, sortDir: sort.dir })
      .then((res) => {
        setItems(res.items || []);
        setTotal(res.total || 0);
      })
      .catch((err) => setError(errorMessage(err, "Could not load the login history.")))
      .finally(() => setLoading(false));
  }, [offset, search, userId, sort]);

  const page = Math.floor(offset / LOGIN_PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / LOGIN_PAGE_SIZE));
  const onSort = (key) => setSort((prev) => nextSort(prev, key));

  return (
    <div>
      <div className="section-toolbar">
        <input
          className="search-input"
          placeholder="Search by user email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={Boolean(userId)}
        />
        {userId && (
          <span className="count-label">
            Filtering by user id <strong>{userId}</strong>{" "}
            <WplButton className="small subtle" value="Clear filter" onClick={onClearUser} />
          </span>
        )}
        <span className="count-label">{total} total</span>
      </div>

      <div className="table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh label="Name" sortKey="firstname" sort={sort} onSort={onSort} />
              <SortableTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
              <SortableTh label="Login time" sortKey="timestamp_created" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="table-message" colSpan={3}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td className="table-message" colSpan={3}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && items.length === 0 && (
              <tr>
                <td className="table-message" colSpan={3}>
                  No login entries found.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((row) => (
                <tr key={row.id}>
                  <td>{row.firstname || "—"}</td>
                  <td>{row.email}</td>
                  <td title={row.timestamp_created ? toPrettyDateStr(new Date(row.timestamp_created), true) : ""}>
                    {formatLastLogin(row.timestamp_created)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && total > LOGIN_PAGE_SIZE && (
        <div className="section-toolbar">
          <WplButton
            className="small subtle"
            value="Previous"
            onClick={() => setOffset(Math.max(0, offset - LOGIN_PAGE_SIZE))}
            disabled={offset === 0}
          />
          <span className="count-label">
            Page {page} / {totalPages}
          </span>
          <WplButton
            className="small subtle"
            value="Next"
            onClick={() => setOffset(offset + LOGIN_PAGE_SIZE)}
            disabled={offset + LOGIN_PAGE_SIZE >= total}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Users tab

const USER_SORT_ACCESSORS = {
  last_login: (u) => (u.last_login ? new Date(u.last_login).getTime() : null),
};

function UsersSection({ users, canDelete, me, onEdit, onDelete, onViewLogins }) {
  const [search, setSearch] = useState("");
  const [sort, onSort] = useClientSort({ key: "email", dir: "asc" });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.email.toLowerCase().includes(q) || (u.firstname || "").toLowerCase().includes(q) || (u.scope || "").includes(q),
    );
  }, [users, search]);
  const sorted = useMemo(() => sortRows(filtered, sort, USER_SORT_ACCESSORS), [filtered, sort]);

  return (
    <div>
      <div className="section-toolbar">
        <input
          className="search-input"
          placeholder="Search by email, name or scope…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="count-label">
          {filtered.length} / {users.length} users
        </span>
      </div>

      <div className="table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
              <SortableTh label="Name" sortKey="firstname" sort={sort} onSort={onSort} />
              <SortableTh label="Scope" sortKey="scope" sort={sort} onSort={onSort} />
              <SortableTh label="Last login" sortKey="last_login" sort={sort} onSort={onSort} />
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.firstname || "—"}</td>
                <td>
                  <ScopeBadge scope={u.scope} />
                </td>
                <td title={u.last_login ? toPrettyDateStr(new Date(u.last_login), true) : ""}>{formatLastLogin(u.last_login)}</td>
                <td className="actions-cell">
                  <WplButton className="small" value="Edit" onClick={() => onEdit(u)} />
                  <WplButton className="small subtle" value="View logins" onClick={() => onViewLogins(u.id)} />
                  {canDelete && me && me.email !== u.email && (
                    <WplButton className="small danger-outline" value="Delete" onClick={() => onDelete(u)} />
                  )}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td className="table-message" colSpan={5}>
                  No users match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------- Deleted users tab

function DeletedUsersSection({ users }) {
  const [search, setSearch] = useState("");
  const [sort, onSort] = useClientSort({ key: "original_email", dir: "asc" });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => (u.original_email || u.email).toLowerCase().includes(q) || (u.firstname || "").toLowerCase().includes(q),
    );
  }, [users, search]);
  const sorted = useMemo(() => sortRows(filtered, sort, USER_SORT_ACCESSORS), [filtered, sort]);

  return (
    <div>
      <p className="section-hint">
        Deleted users keep their reports and history but can no longer log in. Their email address is free to be invited
        again.
      </p>
      <div className="section-toolbar">
        <input className="search-input" placeholder="Search by email or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="count-label">
          {filtered.length} / {users.length} deleted users
        </span>
      </div>

      <div className="table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh label="Email" sortKey="original_email" sort={sort} onSort={onSort} />
              <SortableTh label="Name" sortKey="firstname" sort={sort} onSort={onSort} />
              <SortableTh label="Scope" sortKey="scope" sort={sort} onSort={onSort} />
              <SortableTh label="Last login" sortKey="last_login" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.id} className="deleted-row">
                <td title={u.email}>{u.original_email || u.email}</td>
                <td>{u.firstname || "—"}</td>
                <td>
                  <ScopeBadge scope={u.scope} />
                </td>
                <td>{formatLastLogin(u.last_login)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td className="table-message" colSpan={4}>
                  No deleted users.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Invitations tab

function InvitationsSection({ invitations, users, onShowLink }) {
  const [search, setSearch] = useState("");
  const [sort, onSort] = useClientSort({ key: "status", dir: "desc" });
  const [busyId, setBusyId] = useState(null);

  // An invitation counts as accepted when a user row (deleted or not) points at it.
  const acceptedIds = useMemo(() => new Set(users.map((u) => u.invitation_id).filter(Boolean)), [users]);

  const rows = useMemo(
    () =>
      invitations.map((inv) => {
        let windfarmCount = 0;
        try {
          windfarmCount = JSON.parse(inv.windfarm_ids || "[]").length;
        } catch (e) {
          windfarmCount = 0;
        }
        const accepted = acceptedIds.has(inv.id);
        const isLassie = Boolean(inv.lassie_access);
        return { ...inv, accepted, isLassie, windfarmCount, status: accepted ? "accepted" : "pending", canResend: !accepted };
      }),
    [invitations, acceptedIds],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(q) || (r.user_name || "").toLowerCase().includes(q) || r.status.includes(q));
  }, [rows, search]);
  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);

  function resend(inv) {
    setBusyId(inv.id);
    userAPI
      .resendInvitation(inv.id)
      .then((link) =>
        onShowLink({
          title: "Invitation resent",
          text: `A new invitation email was sent to ${inv.email}. You can also share the link directly:`,
          link,
        }),
      )
      .catch((err) => alert(errorMessage(err, "Could not resend the invitation.")))
      .finally(() => setBusyId(null));
  }

  return (
    <div>
      <div className="section-toolbar">
        <input
          className="search-input"
          placeholder="Search by email, name or status…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="count-label">
          {filtered.length} / {rows.length} invitations
        </span>
      </div>

      <div className="table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <SortableTh label="Name" sortKey="user_name" sort={sort} onSort={onSort} />
              <SortableTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
              <SortableTh label="Scope" sortKey="scope" sort={sort} onSort={onSort} />
              <SortableTh label="Lassie" sortKey="isLassie" sort={sort} onSort={onSort} />
              <SortableTh label="Wind farms" sortKey="windfarmCount" sort={sort} onSort={onSort} />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.user_name || "—"}</td>
                <td>{inv.email}</td>
                <td>
                  <ScopeBadge scope={inv.scope} />
                </td>
                <td>{inv.isLassie ? (inv.is_lassie_organization_admin ? "Admin" : "Yes") : "—"}</td>
                <td>{inv.windfarmCount}</td>
                <td>
                  <span className={`status-badge ${inv.status}`}>{inv.accepted ? "Accepted" : "Pending"}</span>
                </td>
                <td className="actions-cell">
                  {inv.canResend && (
                    <WplButton className="small" value="Resend" loading={busyId === inv.id} onClick={() => resend(inv)} />
                  )}
                  {inv.canResend && (
                    <WplButton
                      className="small subtle"
                      value="Copy link"
                      onClick={() =>
                        onShowLink({
                          title: "Invitation link",
                          text: `Share this link with ${inv.email}:`,
                          link: `${window.location.origin}/invitation/${inv.token}`,
                        })
                      }
                    />
                  )}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td className="table-message" colSpan={7}>
                  No invitations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Edit user panel

function EditUserPanel({ user, windfarms, canDelete, onUsersChanged, onDelete, onClose }) {
  const [scope, setScope] = useState(user.scope);
  const [savingScope, setSavingScope] = useState(false);
  const [scopeMessage, setScopeMessage] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [resetting, setResetting] = useState(false);
  const [userWindfarms, setUserWindfarms] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    userAPI
      .listUserWindfarms(user.id)
      .then(setUserWindfarms)
      .catch(() => setUserWindfarms([]));
  }, [user.id]);

  const assignedIds = useMemo(() => new Set((userWindfarms || []).map((wf) => wf.id)), [userWindfarms]);

  function saveScope() {
    setSavingScope(true);
    setError("");
    userAPI
      .updateScope(user.id, scope)
      .then(() => {
        setScopeMessage(`Scope updated to ${scope}.`);
        return onUsersChanged();
      })
      .catch((err) => setError(errorMessage(err, "Could not update the scope.")))
      .finally(() => setSavingScope(false));
  }

  function createResetLink() {
    if (!window.confirm(`Create a password reset link for ${user.email}?`)) return;
    setResetting(true);
    setError("");
    userAPI
      .createResetPasswordRequest(user.id)
      .then((link) => setResetLink(typeof link === "string" ? link : JSON.stringify(link)))
      .catch((err) => setError(errorMessage(err, "Could not create the reset link.")))
      .finally(() => setResetting(false));
  }

  function toggleWindfarm(wf) {
    setError("");
    const call = assignedIds.has(wf.id) ? userAPI.removeUserFromWindfarm(user.id, wf.id) : userAPI.addUserToWindfarm(user.id, wf.id);
    return call.then(setUserWindfarms).catch((err) => setError(errorMessage(err, "Could not update wind farm access.")));
  }

  return (
    <div className="edit-user-panel">
      <h2>{user.firstname || user.email}</h2>
      <p className="user-email">
        {user.email} · <ScopeBadge scope={user.scope} />
      </p>

      <section>
        <h3>Scope</h3>
        <div className="inline-editor">
          <label>
            Scope
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              {SCOPES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <WplButton className="primary" value="Save" loading={savingScope} disabled={scope === user.scope} onClick={saveScope} />
          {scopeMessage && <span className="hint">{scopeMessage}</span>}
        </div>
      </section>

      <section>
        <h3>Password</h3>
        <div className="inline-editor">
          <WplButton value="Create reset link" loading={resetting} onClick={createResetLink} />
          <span className="hint">Creates a one-time link you can send to the user.</span>
        </div>
        {resetLink && (
          <div className="link-display">
            <code>{resetLink}</code>
            <CopyButton text={resetLink} />
          </div>
        )}
      </section>

      <section>
        <h3>Wind farm access</h3>
        <WindfarmToggleList windfarms={windfarms} assignedIds={assignedIds} onToggle={toggleWindfarm} loading={userWindfarms === null} />
      </section>

      {error && <p className="error-message">{error}</p>}

      <div className="panel-actions">
        {canDelete && <WplButton className="danger-outline" value="Delete user" onClick={onDelete} />}
        <WplButton className="subtle" value="Close" onClick={onClose} />
      </div>
    </div>
  );
}

// ------------------------------------------------------- Create invitation

function CreateInvitationForm({ windfarms, rockyOrganizations, lassieOrganizations, onDone }) {
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("");
  const [rockyOrganization, setRockyOrganization] = useState("");
  const [selectedWindfarms, setSelectedWindfarms] = useState([]);
  const [lassieAccess, setLassieAccess] = useState(false);
  const [lassieOrganization, setLassieOrganization] = useState("");
  const [lassieAdmin, setLassieAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const windfarmNames = useMemo(() => [...windfarms].sort(byName).map((wf) => wf.name), [windfarms]);
  const rockyNames = useMemo(() => [...rockyOrganizations].sort(byName).map((o) => o.name), [rockyOrganizations]);
  const lassieNames = useMemo(() => [...lassieOrganizations].sort(byName).map((o) => o.name), [lassieOrganizations]);

  const isRocky = scope === "rocky";
  const showWindfarms = scope && !isRocky;

  function changeScope(newScope) {
    setScope(newScope);
    setRockyOrganization("");
    setLassieOrganization("");
    setSelectedWindfarms([]);
  }

  function validate() {
    if (!userName.trim()) return "First name is required.";
    if (!EMAIL_RE.test(email.trim())) return "Enter a valid email address.";
    if (!scope) return "Select a scope.";
    if (isRocky && !rockyOrganization) return "Rocky users need a Rocky organization.";
    return "";
  }

  function submit() {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSubmitting(true);

    const windfarmIds = selectedWindfarms.map((name) => windfarms.find((wf) => wf.name === name)?.id).filter(Boolean);
    const rockyId = rockyOrganization ? rockyOrganizations.find((o) => o.name === rockyOrganization)?.id ?? null : null;
    const lassieId = lassieOrganization ? lassieOrganizations.find((o) => o.name === lassieOrganization)?.id ?? null : null;
    const hasLassie = showWindfarms && lassieAccess;

    userAPI
      .createUser(userName.trim(), email.trim(), windfarmIds, hasLassie, scope, lassieId, hasLassie ? lassieAdmin : null, rockyId)
      .then((res) => {
        const terms = hasLassie ? " The user must accept the Terms of Service and set a password." : " The user must set a password.";
        onDone({
          title: "Invitation sent",
          text: `An invitation email was sent to ${email.trim()}.${terms} You can also share the link directly:`,
          link: typeof res === "string" ? res : "",
        });
      })
      .catch((err) => setError(errorMessage(err, "Could not create the invitation.")))
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="create-invitation-panel">
      <h2>Create invitation</h2>
      <p className="section-hint">
        The invitee receives an email with a link to complete their registration and choose a password. With Lassie
        access they must also accept the Terms of Service first.
      </p>

      <div className="create-form">
        <label>
          First name
          <input value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="e.g. Jane" />
        </label>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@company.com" />
        </label>
        <label>
          Scope
          <select value={scope} onChange={(e) => changeScope(e.target.value)}>
            <option value="" disabled>
              Select a scope
            </option>
            {SCOPES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        {isRocky && (
          <label>
            Rocky organization
            <select value={rockyOrganization} onChange={(e) => setRockyOrganization(e.target.value)}>
              <option value="" disabled>
                Select an organization
              </option>
              {rockyNames.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}

        {showWindfarms && (
          <WplInput
            title="Wind farms"
            className="windfarm-picker"
            placeholder="Type to search wind farms"
            options={windfarmNames}
            onlySelectableOptions
            multiSelect
            value={selectedWindfarms}
            onChanged={setSelectedWindfarms}
          />
        )}

        {showWindfarms && (
          <div className="switch-row">
            <WplSwitch
              title="Lassie access"
              value={lassieAccess}
              onChanged={(v) => {
                setLassieAccess(v);
                if (!v) {
                  setLassieAdmin(false);
                  setLassieOrganization("");
                }
              }}
            />
            {lassieAccess && <WplSwitch title="Lassie organization admin" value={lassieAdmin} onChanged={setLassieAdmin} />}
          </div>
        )}

        {showWindfarms && lassieAccess && (
          <label>
            Lassie organization
            <select value={lassieOrganization} onChange={(e) => setLassieOrganization(e.target.value)}>
              <option value="">No organization</option>
              {lassieNames.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && <p className="error-message">{error}</p>}

        <div className="submit-row">
          <WplButton
            className="primary"
            value="Send invitation"
            loading={submitting}
            onClick={submit}
          />
        </div>
      </div>
    </div>
  );
}
